import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { MetalsPricingService, MetalPrice } from '../../services/metals-pricing.service';
import { buildDashboardThemeVars } from '../dashboard-widget-theme';

@Component({
	selector: 'app-metal-prices',
	templateUrl: './metal-prices.component.html',
	styleUrls: ['./metal-prices.component.scss'],
	animations: [
		trigger('flipState', [
			state('active', style({
				transform: 'rotate3d(0, 1, 0, 179deg)',
			})),
			state('inactive', style({
				transform: 'rotateY(0)'
			})),
			transition('active => inactive', animate('400ms ease-out')),
			transition('inactive => active', animate('400ms ease-in')),
		]),
		trigger('openClose', [
			state(':enter', style({ height: '*' })),
			state(':leave', style({ height: '0px' })),
			transition('false <=> true', animate(500))
		]),
	]
})
export class MetalPricesComponent implements OnInit, OnDestroy {
	@Input() flip = 'inactive';
	@Output() viewData = new EventEmitter<any>();

	metalPrices: MetalPrice[] = [];
	groupedMetals: { [key: string]: MetalPrice[] } = {};
	splitColumns: { [key: string]: MetalPrice[][] } = {};
	loading = false;
	error: string | null = null;
	lastUpdated: Date | null = null;
	viewing: any = false;
	title = 'Metal Prices';

	get themeVars(): Record<string, string> {
		return buildDashboardThemeVars('mp', 'success');
	}

	constructor(private metalsPricingService: MetalsPricingService) {}

	ngOnInit(): void {
		this.loadMetalPrices();
	}

	ngOnDestroy(): void {
		// Cleanup if needed
	}

	/**
	 * Load metal prices from API
	 */
	loadMetalPrices(): void {
		this.loading = true;
		this.error = null;

		this.metalsPricingService.getMetalPrices().subscribe({
			next: (response) => {
				if (response.success && response.data) {
					this.metalPrices = this.metalsPricingService.formatMetalPrices(response);
					this.groupMetals();
					this.lastUpdated = new Date();
					this.loading = false;
				} else {
					this.error = response.error || 'Failed to fetch metal prices';
					this.loading = false;
				}
			},
			error: (err) => {
				this.error = err?.error?.error || err?.error || err?.message || 'Failed to connect to metal pricing service';
				this.loading = false;
			}
		});
	}

	/**
	 * Group metals by category
	 */
	groupMetals(): void {
		this.groupedMetals = {};
		this.splitColumns = {};
		
		this.metalPrices.forEach(metal => {
			let category = 'Other';
			
			if (metal.metal === 'Gold' || metal.metal === 'Silver' || metal.metal === 'Platinum' || metal.metal === 'Palladium') {
				category = 'Precious Metals';
			} else if (metal.metal.startsWith('LBMA')) {
				category = 'LBMA';
			} else if (metal.metal.startsWith('MCX')) {
				category = 'MCX';
			} else if (metal.metal.startsWith('LME')) {
				category = 'LME';
			} else if (metal.metal === 'IBJA Gold') {
				category = 'IBJA';
			} else if (['Copper', 'Aluminum', 'Lead', 'Nickel', 'Zinc'].includes(metal.metal)) {
				category = 'Base Metals';
			}
			
			if (!this.groupedMetals[category]) {
				this.groupedMetals[category] = [];
			}
			this.groupedMetals[category].push(metal);
		});

		// Pre-compute split columns for each category
		Object.keys(this.groupedMetals).forEach(category => {
			this.splitColumns[category] = this.splitIntoColumns(this.groupedMetals[category]);
		});
	}

	/**
	 * Get category order for display
	 */
	getCategoryOrder(): string[] {
		return ['Precious Metals', 'LBMA', 'MCX', 'IBJA', 'Base Metals', 'LME', 'Other'];
	}

	/**
	 * Track by function for ngFor
	 */
	trackByIndex(index: number): number {
		return index;
	}

	/**
	 * Split array into chunks for 2-column display
	 */
	splitIntoColumns(items: MetalPrice[] | undefined): MetalPrice[][] {
		if (!items || items.length === 0) {
			return [[], []];
		}
		const mid = Math.ceil(items.length / 2);
		return [items.slice(0, mid), items.slice(mid)];
	}

	/**
	 * Manual refresh button handler
	 */
	refreshPrices(): void {
		this.loadMetalPrices();
	}

	/**
	 * Toggle flip animation for detailed view
	 */
	toggleFlip(data: any, title: string): void {
		if (!data) {
			this.flip = 'inactive';
		} else {
			this.flip = (this.flip === 'active') ? 'inactive' : 'active';
		}

		if (this.flip === 'active') {
			this.viewing = data;
			this.title = title;
			// Emit 'Metal Prices' string for dashboard switch case
			this.viewData.emit('Metal Prices');
		} else {
			this.viewData.emit(false);
		}
	}

	/**
	 * Format price for display
	 */
	formatPrice(price: number): string {
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
			minimumFractionDigits: 2,
			maximumFractionDigits: 2
		}).format(price);
	}

	/**
	 * Get metal icon class
	 */
	getMetalIcon(metal: string): string {
		const icons: { [key: string]: string } = {
			'Gold': 'monetization_on',
			'Silver': 'brightness_1',
			'Platinum': 'diamond',
			'Palladium': 'circle',
			'LBMA Gold AM': 'monetization_on',
			'LBMA Gold PM': 'monetization_on',
			'LBMA Silver': 'brightness_1',
			'LBMA Platinum AM': 'diamond',
			'LBMA Platinum PM': 'diamond',
			'LBMA Palladium AM': 'circle',
			'LBMA Palladium PM': 'circle',
			'MCX Gold': 'monetization_on',
			'MCX Gold AM': 'monetization_on',
			'MCX Gold PM': 'monetization_on',
			'MCX Silver': 'brightness_1',
			'MCX Silver AM': 'brightness_1',
			'MCX Silver PM': 'brightness_1',
			'IBJA Gold': 'monetization_on',
			'Copper': 'settings',
			'Aluminum': 'layers',
			'Lead': 'square',
			'Nickel': 'radio_button_checked',
			'Zinc': 'hexagon',
			'LME Copper': 'settings',
			'LME Aluminum': 'layers',
			'LME Lead': 'square',
			'LME Nickel': 'radio_button_checked',
			'LME Zinc': 'hexagon'
		};
		return icons[metal] || 'monetization_on';
	}

	/**
	 * Get metal color class
	 */
	getMetalColorClass(metal: string): string {
		const colors: { [key: string]: string } = {
			'Gold': 'gold',
			'Silver': 'silver',
			'Platinum': 'platinum',
			'Palladium': 'palladium',
			'LBMA Gold AM': 'gold',
			'LBMA Gold PM': 'gold',
			'LBMA Silver': 'silver',
			'LBMA Platinum AM': 'platinum',
			'LBMA Platinum PM': 'platinum',
			'LBMA Palladium AM': 'palladium',
			'LBMA Palladium PM': 'palladium',
			'MCX Gold': 'gold',
			'MCX Gold AM': 'gold',
			'MCX Gold PM': 'gold',
			'MCX Silver': 'silver',
			'MCX Silver AM': 'silver',
			'MCX Silver PM': 'silver',
			'IBJA Gold': 'gold',
			'Copper': 'copper',
			'Aluminum': 'aluminum',
			'Lead': 'lead',
			'Nickel': 'nickel',
			'Zinc': 'zinc',
			'LME Copper': 'copper',
			'LME Aluminum': 'aluminum',
			'LME Lead': 'lead',
			'LME Nickel': 'nickel',
			'LME Zinc': 'zinc'
		};
		return colors[metal] || '';
	}
}


